<template>
    <ul class="m-0 list-none space-y-2 p-0">
        <li
            v-for="(selection, index) in highlights"
            :key="index"
            class="flex items-start rounded-lg border-l-[3px] bg-black/[0.02] p-3 dark:bg-white/[0.03]"
            :style="{ borderLeftColor: selection.color }"
        >
            <div class="min-w-0 flex-1">
                <div class="flex items-center">
                    <svg-icon
                        v-if="ui.listening.opened && ui.listening.uuidToListen === selection.uuid"
                        class="mr-2"
                        icon="baseline-volume_up-24px"
                        width="16px"
                        height="16px"
                    />
                </div>

                <button
                    type="button"
                    class="cursor-pointer border-none bg-transparent p-0 text-left text-[13px] leading-relaxed text-theme-text hover:opacity-70 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    @click="maybeGoToPage(selection)"
                >
                    <TruncatedParagraph
                        :text="selection.totalText"
                        :is-annotation="false"
                        :highlight-color="selection.color"
                    />
                </button>

                <p
                    v-if="selection.annotationText"
                    class="mt-1.5 border-l-2 pl-2 text-[13px] italic text-theme-toolbar-icon"
                    :style="{ borderLeftColor: selection.color }"
                >
                    {{ selection.annotationText }}
                </p>

                <p v-if="isPdf && selection.startPage" class="mt-1.5 text-[11px] text-theme-toolbar-icon opacity-60">
                    p. {{ selection.startPage }}
                </p>
            </div>

            <div class="ml-2 shrink-0">
                <ActionMenu :selection="selection" />
            </div>
        </li>
    </ul>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import TruncatedParagraph from './TruncatedParagraph.vue';
import ActionMenu from './ActionMenu.vue';

export default {
    components: {
        TruncatedParagraph,
        ActionMenu,
    },
    props: {
        highlights: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        ...mapState(['ui']),
        ...mapGetters(['isEpub', 'isPdf', 'onlyMobileVisible']),
    },
    methods: {
        ...mapActions(['goToPage', 'goToLocation', 'toggleNotebook']),
        /**
         * Navigates to the page or location of the selected highlight.
         * Supports EPUB R2 (CSS selector locator), EPUB R1 (legacy), and PDF.
         * @param {Object} selection - The highlight/note selection object
         */
        maybeGoToPage(selection) {
            if (document.querySelector('.actionMenuWrapper .ActionMenu.isOpen')) {
                return;
            }

            if (this.isEpub) {
                this.goToLocation(this.buildEpubNavigationPayload(selection));
            } else {
                this.goToPage({
                    page: selection.startPage,
                    trigger: 'click',
                    source: 'notes',
                });
            }

            if (this.onlyMobileVisible) {
                this.toggleNotebook();
            }
        },
        /**
         * Builds the navigation payload for EPUB highlights.
         * R2 highlights pass epubR2Location with href, type, and locations.
         * R1 legacy highlights use chapter href + character offset for precise positioning.
         * @param {Object} selection - The highlight/note selection object
         * @returns {Object} Payload for goToLocation action
         */
        buildEpubNavigationPayload(selection) {
            const epubR2Location = selection.epubR2Location ?? selection.location;

            // R2 with full locations: use directly
            if (epubR2Location?.locations) {
                return { epubR2Location };
            }

            // R1 legacy: use chapter href + text for precise navigation
            return {
                epubLocation: selection.epubLocation,
                totalText: selection.totalText,
                trigger: 'click',
                source: 'notes',
            };
        },
    },
};
</script>
